/* @flow */

import * as React from 'react';
import Helmet from 'react-helmet';
import { get } from 'lodash/fp';
import isNil from 'lodash/isNil';
import moment from 'moment';
import { connect } from 'react-redux';
import classNames from 'classnames';

import t from 'app/utils/translate';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import Tappable from 'app/components/commons/Tappable';
import { createLimits } from '../../actions/userActions';
import Button from '../../components/commons/Button';
import Grid from '../../components/layout/Grid';
import GridCol from '../../components/layout/GridCol';
import FormValidationMessage from '../../components/commons/FormValidationMessage';
import LimitCommitment from '../../components/user/LimitCommitment';
import MoneySelector from '../../components/user/MoneySelector';
import MessageModal from '../../components/user/MessageModal';

type Props = {
  endDate?: string,
  hasLightBackground: boolean,
  isSubmitting: boolean,
  onSubmit: Object => void,
  savedDepositLeft?: number,
  savedDepositLimit?: number,
  savedDurationLimit?: number,
  savedSpendingLeft?: number,
  savedSpendingLimit?: number,
  submissionError?: string,
  isRestricted?: boolean,
  restrictedEndDate?: string,
  hasLimit?: boolean
};

type State = {
  depositLimit?: number,
  durationLimit?: number,
  messageModalIsOpen: boolean,
  messageTitle: string,
  notificationMessage: string,
  showEditor: boolean,
  spendingLimit?: number,
  validationError?: string
};

const durations = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' }
];
const depositAmounts = [
  { value: null, label: 'No Limit' },
  { value: 0, label: '$0' },
  { value: 100, label: '$100' },
  { value: 500, label: '$500' },
  { value: 1000, label: '$1000' }
];

class AccountLimits extends React.Component<Props, State> {
  props: Props;

  state: State;

  static defaultProps = {
    endDate: undefined,
    savedDepositLeft: undefined,
    savedDepositLimit: undefined,
    savedDurationLimit: 7,
    savedSpendingLeft: undefined,
    savedSpendingLimit: undefined,
    submissionError: undefined,
    isRestricted: false,
    restrictedEndDate: undefined,
    hasLimit: false
  };

  constructor(props: Props, ...args: Array<*>) {
    super(props, ...args);

    this.state = {
      depositLimit: props.savedDepositLimit,
      durationLimit: props.savedDurationLimit,
      messageModalIsOpen: false,
      messageTitle: '',
      notificationMessage: '',
      showEditor: isNil(props.savedDepositLimit) || isNil(props.savedSpendingLimit),
      spendingLimit: props.savedSpendingLimit,
      validationError: undefined
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { isSubmitting } = this.props;

    if (isSubmitting && !nextProps.isSubmitting) {
      this.setState({
        depositLimit: nextProps.savedDepositLimit,
        durationLimit: nextProps.savedDurationLimit,
        messageModalIsOpen: false,
        spendingLimit: nextProps.savedSpendingLimit
      });
    }
  }

  handleApplyClick() {
    const {
      savedDepositLimit,
      savedDurationLimit,
      savedSpendingLimit,
      isRestricted,
      restrictedEndDate,
      hasLimit
    } = this.props;
    const { depositLimit, durationLimit, spendingLimit } = this.state;

    // Not set any limit yet
    if (!hasLimit) {
      if (isNil(depositLimit) && isNil(spendingLimit)) {
        this.setState({
          validationError: 'You must select either a deposit limit, a spending limit, or both.'
        });
        return;
      }
    }

    // Limits are restricted to be updated
    if (isRestricted) {
      const restrictedDateText = restrictedEndDate
        ? `Please try again after ${moment(restrictedEndDate).format('D MMM YYYY')}.`
        : '';

      if (!isNil(savedDepositLimit)) {
        if (isNil(depositLimit) || depositLimit > savedDepositLimit) {
          this.setState({
            validationError: `You can not clear deposit limit or set it larger than $${savedDepositLimit} at the moment. ${restrictedDateText}`
          });
          return;
        }
      }

      if (!isNil(savedSpendingLimit)) {
        if (isNil(spendingLimit) || spendingLimit > savedSpendingLimit) {
          this.setState({
            validationError: `You can not clear spending limit or set it larger than $${savedSpendingLimit} at the moment. ${restrictedDateText}`
          });
          return;
        }
      }

      if (!isNil(savedDurationLimit) && durationLimit < savedDurationLimit) {
        this.setState({
          validationError: `Duration can not be shorter than what you have set at the moment. ${restrictedDateText}`
        });
        return;
      }

      if (isNil(depositLimit) && isNil(spendingLimit)) {
        this.setState({
          validationError: `Unable to clear the limits at the moment. ${restrictedDateText}`
        });
        return;
      }
    }

    this.setState({
      messageModalIsOpen: true,
      messageTitle: 'Confirm limits',
      notificationMessage: `Please note that this is a firm commitment. You will not be able to
          increase your deposit or spend limits until your chosen timeframe has expired. You may,
          however, reduce your maximum deposit or spend limits during the selected time frame.`
    });
  }

  handleSubmit = () => {
    const { depositLimit, durationLimit, spendingLimit } = this.state;
    const { onSubmit } = this.props;

    onSubmit({
      deposit: depositLimit,
      duration: durationLimit,
      spending: spendingLimit
    });
  };

  handleInputChange = (key, value) => {
    this.setState({
      [key]: value,
      validationError: undefined
    });
  };

  closeMessageModal = () => {
    this.setState({
      messageModalIsOpen: false
    });
  };

  render() {
    const {
      endDate,
      hasLightBackground,
      isSubmitting,
      savedDepositLeft,
      savedDepositLimit,
      savedSpendingLeft,
      savedSpendingLimit,
      submissionError,
      isRestricted,
      restrictedEndDate
    } = this.props;

    const {
      depositLimit,
      durationLimit,
      messageModalIsOpen,
      messageTitle,
      notificationMessage,
      showEditor,
      spendingLimit,
      validationError
    } = this.state;

    const displayCommitment = !isNil(savedDepositLimit) || !isNil(savedSpendingLimit);
    const scheme = hasLightBackground ? 'light' : 'dark';

    return (
      <div className="page-container">
        <Helmet title="Limits" />
        <NotificationHandler
          type={submissionError ? 'danger' : 'success'}
          afterCond={!isSubmitting}
          beforeCond={isSubmitting}
        >
          {submissionError || t('Limits successfully applied')}
        </NotificationHandler>
        {displayCommitment && (
          <LimitCommitment
            deposit={savedDepositLimit}
            depositLeft={savedDepositLeft}
            displayEditButton={!showEditor}
            endDate={endDate}
            isRestricted={isRestricted}
            onEdit={() => this.setState({ showEditor: true })}
            spending={savedSpendingLimit}
            spendingLeft={savedSpendingLeft}
            restrictedEndDate={isRestricted && restrictedEndDate ? restrictedEndDate : undefined}
          />
        )}
        {showEditor && (
          <div>
            <Grid>
              <GridCol width="one-half">
                <span>Limit my activity for:</span>
                <div className="form-field-group">
                  <div className="toggle-buttons">
                    {durations.map(d => (
                      <Tappable
                        key={d.value}
                        className={classNames('toggle-button', {
                          'toggle-button-active': durationLimit === d.value
                        })}
                        onTap={() => this.handleInputChange('durationLimit', d.value)}
                      >
                        {d.label}
                      </Tappable>
                    ))}
                  </div>
                </div>
              </GridCol>
            </Grid>
            <MoneySelector
              label="And limit my deposits to"
              display={depositAmounts}
              value={depositLimit}
              onChange={value => this.handleInputChange('depositLimit', value)}
              scheme={scheme}
            />
            <MoneySelector
              label="And limit my spending to"
              display={depositAmounts}
              value={spendingLimit}
              onChange={value => this.handleInputChange('spendingLimit', value)}
              scheme={scheme}
            />
            {validationError && <FormValidationMessage message={validationError} />}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Button
                block
                size="medium"
                onClick={() => this.handleApplyClick()}
                style={{
                  width: '130px'
                }}
              >
                Apply
              </Button>
            </div>
            {messageModalIsOpen && (
              <MessageModal
                message={notificationMessage}
                messageTitle={messageTitle}
                isOpen={messageModalIsOpen}
                onCancel={this.closeMessageModal}
                onConfirm={this.handleSubmit}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const limit = get('user.currentUser.limit', state) || {};

  return {
    endDate: limit.end_date,
    id: limit.id,
    isSubmitting: state.user.updatingLimits,
    savedDepositLeft: limit.deposit_left,
    savedDepositLimit: limit.deposit,
    savedDurationLimit: limit.duration ? parseInt(limit.duration, 10) : undefined,
    savedSpendingLeft: limit.spending_left,
    savedSpendingLimit: limit.spending,
    submissionError: state.user.updatingLimitsErrorMessage,
    hasLightBackground: !state.settings.darkMode,
    isRestricted: limit.is_restricted,
    restrictedEndDate: get('restricted_end_date.date', limit) || get('restricted_end_date', limit),
    hasLimit: !!limit.id
  };
}

const actions = {
  onSubmit: createLimits
};

const connector = connect(
  mapStateToProps,
  actions
);

export default connector(AccountLimits);
